import { Check, X, Scale, ArrowRight, ExternalLink } from "lucide-react";
import { getDisplayData } from "@/utils/formatFirmDisplay";
import { getAffiliateLink, trackAffiliateClick } from "@/utils/tracking";
import { VerifiedTraderRating } from "@/components/common/VerifiedTraderRating";
import { getSizeNumeric } from "@/utils/sizeUtils";
import { formatFirmDisplay } from "@/utils/formatFirmDisplay";
import { isPublicEvalType } from "@/utils/constants";

const ModalCTA = ({ href, label, firmSlug, ...rest }) => {
  if (!href) return null;
  return (
    <span className="inline-flex flex-col items-center">
      <a
        href={href}
        target="_blank"
        rel="sponsored nofollow noopener noreferrer"
        title="Opens official site"
        onClick={() => { try { trackAffiliateClick(label, firmSlug || "", href, "comparison_table"); } catch {} }}
        className="group inline-flex no-underline active:scale-[0.99] hover:-translate-y-0.5 transition-all duration-300"
        style={{ padding: "2.5px", borderRadius: "16px", background: "linear-gradient(135deg, #ef4444, #f59e0b 30%, #a78bfa 70%, #3b82f6)", boxShadow: "0 4px 16px rgba(239,68,68,0.10), 0 4px 16px rgba(59,130,246,0.10)" }}
        {...rest}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "10px 22px", borderRadius: "calc(16px - 2.5px)", background: "linear-gradient(145deg, rgba(240,242,248,0.95), rgba(230,235,242,0.92))", backdropFilter: "blur(16px)", color: "#0f172a", fontSize: "13px", fontWeight: 600, whiteSpace: "nowrap" }}>
          {label}
          <span className="relative inline-flex items-center" style={{ width: 14, height: 14 }}>
            <ArrowRight size={14} strokeWidth={2.2} className="absolute inset-0 transition-all duration-200 group-hover:opacity-0 group-hover:translate-x-1" style={{ opacity: 0.85 }} />
            <ExternalLink size={12} strokeWidth={2.2} className="absolute inset-0 transition-all duration-200 opacity-0 -translate-x-1 group-hover:opacity-85 group-hover:translate-x-0" />
          </span>
        </span>
      </a>
      <span className="text-[10px] text-slate-500 mt-1">Official site</span>
    </span>
  );
};

export const CompareModal = ({ firms, selectedSize, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-slate-900 border border-slate-700/50 rounded-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <div className="flex items-center gap-2">
            <Scale size={18} className="text-[#2979FF]" />
            <h2 className="text-lg font-bold text-white">Compare {firms.length} Firms</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-lg transition-colors">
            <X size={18} className="text-slate-400" />
          </button>
        </div>

        <div className="p-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] text-slate-500 uppercase tracking-wider border-b border-white/5">
                <th className="text-left py-3 px-3">Firm</th>
                <th className="text-left py-3 px-3">Price</th>
                <th className="text-left py-3 px-3">Profit Split</th>
                <th className="text-left py-3 px-3">Drawdown</th>
                <th className="text-left py-3 px-3">Min Days</th>
                <th className="text-left py-3 px-3">Payout</th>
                <th className="text-left py-3 px-3">News</th>
                <th className="text-left py-3 px-3">Algos</th>
              </tr>
            </thead>
            <tbody>
              {firms.map(firm => {
                const fd = getDisplayData(firm);
                const p = firm.pricing?.find(pr => getSizeNumeric(pr) === selectedSize && isPublicEvalType(pr.evaluation_type));
                const fDisp = formatFirmDisplay(firm, p);
                return (
                  <tr key={firm.id} className="border-b border-white/5">
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium text-xs">{firm.name}</span>
                        <VerifiedTraderRating rating={firm.verified_trader_rating} tone="dark" showLabel={false} size="xs" />
                      </div>
                    </td>
                    <td className="py-3 px-3 font-mono text-xs">
                      <span className="text-emerald-400">{fDisp.pricePrimary}</span>
                      {fDisp.discountValid && fDisp.priceOriginal && (
                        <span className="ml-1.5 text-slate-500 line-through text-[10px]">{fDisp.priceOriginal}</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-white text-xs">{fDisp.profitSplit}</td>
                    <td className="py-3 px-3 text-slate-300 text-xs">{fDisp.drawdownShort}</td>
                    <td className="py-3 px-3 text-white text-xs">{fDisp.minEvalDaysShort}</td>
                    <td className="py-3 px-3 text-slate-300 text-xs">{fDisp.payoutShort}</td>
                    <td className="py-3 px-3">{firm.news_trading ? <Check size={14} className="text-emerald-400" /> : <X size={14} className="text-slate-600" />}</td>
                    <td className="py-3 px-3">{firm.algos_allowed ? <Check size={14} className="text-emerald-400" /> : <X size={14} className="text-slate-600" />}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        <div className="p-5 border-t border-white/5 flex flex-wrap gap-3 justify-center">
          {firms.map((firm) => (
              <ModalCTA
                key={firm.id}
                href={getAffiliateLink(firm)}
                label="Get Funded"
                firmSlug={firm.slug}
                data-testid={`compare-get-funded-${firm.slug}`}
              />
          ))}
        </div>
      </div>
    </div>
  );
};
